/**
 * Route Generator Utilities
 * Handles generation of React Router routes from feature tree
 */

import { DYNAMIC_SEGMENT_MAPPING } from './constants';
import { importPageComponent } from './componentLoader';
import { getFeatureComponentPath } from './featureUtils';
import {
  buildDashboardRoute,
  buildCatchAllRoute,
  buildLeafRoute,
  buildParentRoute,
} from './route-builders';

/**
 * Generate route configuration for a single feature
 */
const generateFeatureRoute = (featureWrapper, parentPath = '/admin', parentHandle = null) => {
  const feature = featureWrapper.node || featureWrapper;
  const childrenWrappers = featureWrapper.children || [];

  const fullPath = `${parentPath}/${feature.handle}`;
  const cleanPath = parentPath.startsWith('/') ? parentPath.slice(1) : parentPath;
  const componentPath = getFeatureComponentPath(parentPath, feature, parentHandle);
  const hasChildren = childrenWrappers && childrenWrappers.length > 0;

  // Dashboard special case
  if (feature.handle === 'dashboard' && parentPath === '/admin') {
    return buildDashboardRoute(childrenWrappers, fullPath, feature.handle, generateFeatureRoute);
  }

  // Leaf feature (no children)
  if (!hasChildren) {
    const segmentMapping = DYNAMIC_SEGMENT_MAPPING[feature.handle];
    if (segmentMapping === '...slug') {
      return buildCatchAllRoute(feature.handle, componentPath);
    }
    return buildLeafRoute(feature.handle, componentPath);
  }

  // Parent/group feature with children
  if (hasChildren) {
    return buildParentRoute(feature, childrenWrappers, componentPath, cleanPath, fullPath, generateFeatureRoute);
  }

  // Fallback — simple leaf without CRUD
  const ListComponent = importPageComponent(componentPath);
  return { path: feature.handle, handle: feature.handle, element: <ListComponent />, children: [] };
};

/**
 * Generate all admin routes from feature tree
 * @param {Array} features - Feature tree from API
 * @returns {Array} Route configurations for React Router
 */
export const generateAdminRoutes = (features) => {
  if (!features || !Array.isArray(features) || features.length === 0) {
    console.warn('No features provided to generate routes');
    return [];
  }

  const routes = [];

  features.forEach(feature => {
    try {
      const route = generateFeatureRoute(feature);
      routes.push(route);
    } catch (error) {
      console.error(`Failed to generate route for feature ${feature.handle}:`, error);
    }
  });

  return routes;
};
